import { GeoPlaceType } from "~/schemas/generated/graphql";

interface ValueObject {
  label: string;
  value: string | number;
}

interface MainAddress {
  country?: { id: number };
  fo?: { id: number };
  subject?: { id: number };
  district?: { id: number };
  city?: { id: number };
  locality?: { id: number };
  street?: { id: number };
  house?: { id: number };
  office?: string;
  latitude?: number;
  longitude?: number;
  index?: string;
  phoneCode?: string;
  phone?: string;
  email?: string;
  name?: string;
}

interface AdditionalAddress {
  legalAddress?: MainAddress;
  postAddress?: MainAddress;
}

interface XWeb {
  xshop?: boolean;
  name?: string;
  sto?: MainAddress;
  sale?: MainAddress;
}

interface Lada {
  sto?: MainAddress;
  sale?: MainAddress;
}
interface ASP {
  country?: { id: number };
  fo?: { id: number };
  subject?: { id: number };
  district?: { id: number };
  city?: { id: number };
  locality?: { id: number };
  street?: { id: number };
  house?: { id: number };
  office?: string;
  latitude?: number;
  longitude?: number;
  index?: string;
  phoneCode?: string;
  phone?: string;
  email?: string;
  name?: string;
  inn?: string;
  legalName?: string;
}

interface TransformedData {
  id: number;
  main: MainAddress;
  additional?: AdditionalAddress;
  xweb?: XWeb;
  lada?: Lada;
  asp?: ASP[];
  aspCount: number;
}

interface InputData {
  [key: string]: string | number | boolean | ValueObject | undefined;
}

type TCfg = { type: GeoPlaceType | "Office"; parentField: string | null };

type TAddressFields = Record<string, TCfg>;

export const addressFields: TAddressFields = {
  country: { type: GeoPlaceType.Country, parentField: null },
  fo: { type: GeoPlaceType.Fo, parentField: "country" },
  subject: { type: GeoPlaceType.Subject, parentField: "fo" },
  district: { type: GeoPlaceType.District, parentField: "subject" },
  city: { type: GeoPlaceType.City, parentField: "district" },
  locality: { type: GeoPlaceType.City, parentField: "city" },
  street: { type: GeoPlaceType.Street, parentField: "locality" },
  house: { type: GeoPlaceType.House, parentField: "street" },
};

export const permissionSaveButton = [
  "dealer.updateAddress.main",
  "dealer.updateAddress.legal",
  "dealer.updateAddress.post",
  "dealer.updateAddress.xweb.sto",
  "dealer.updateAddress.xweb.sale",
  "dealer.updateAddress.xweb",
  "dealer.updateAddress.lada.sto",
  "dealer.updateAddress.lada.sale",
  "dealer.updateAddress.asp",
];

export const permissionMap = {
  main: ["dealer.updateAddress.main"],

  additional: {
    legalAddress: ["dealer.updateAddress.legal"],
    postAddress: ["dealer.updateAddress.post"],
  },

  xweb: {
    sto: ["dealer.updateAddress.xweb.sto"],
    sale: ["dealer.updateAddress.xweb.sale"],
    xshop: ["dealer.updateAddress.xweb"],
  },

  lada: {
    sto: ["dealer.updateAddress.lada.sto"],
    sale: ["dealer.updateAddress.lada.sale"],
  },

  asp: ["dealer.updateAddress.asp"],
};

export const transformData = (
  data: InputData,
  id: number,
  aspCount,
): TransformedData => {
  const template: TransformedData = {
    id: id,
    main: {},
  };

  const processAddress = (
    data: InputData,
    prefix: string,
    target: MainAddress | ASP,
  ) => {
    for (const key in data) {
      if (key.startsWith(prefix) && !key.includes("fullAddress")) {
        const fieldName = key.replace(prefix, "");

        if (fieldName === "latitude" || fieldName === "longitude") {
          target[fieldName] = Number(data[key]) ? Number(data[key]) : null;
        } else if (fieldName === "district" && data[key] === null) {
          data[key] = null;
        } else if (
          (fieldName === "locality" && data[key] === null) ||
          data[key] === undefined
        ) {
          data[key] = null;
        } else if (
          typeof data[key] === "object" &&
          data[key] !== null &&
          (data[key] as ValueObject).hasOwnProperty("value")
        ) {
          target[fieldName] = { id: Number((data[key] as ValueObject).value) };
        } else if (
          fieldName.includes("phone") ||
          fieldName.includes("email") ||
          fieldName === "name" ||
          fieldName === "inn" ||
          fieldName === "legalName" ||
          fieldName === "office" ||
          fieldName === "index"
        ) {
          target[fieldName] = String(data[key]) as string;
        } else {
          if (
            data[key] !== "" &&
            data[key] !== null &&
            data[key] !== undefined
          ) {
            const parsedNumber = Number(data[key]);
            if (!isNaN(parsedNumber)) {
              target[fieldName] = parsedNumber;
            }
          }
        }
      }
    }
  };

  processAddress(data, "mainAddress", template.main);

  if (data["legalAddresscountry"] || data["postAddresscountry"]) {
    template.additional = { legalAddress: {}, postAddress: {} };
    processAddress(data, "legalAddress", template.additional.legalAddress);
    processAddress(data, "postAddress", template.additional.postAddress);
  }

  if (data["xwebXshop"] !== undefined) {
    template.xweb = { sto: {}, sale: {} };
    template.xweb.xshop = data.xwebXshop === "Да" ? true : (false as boolean);
    template.xweb.name = data.xwebName as string;
    processAddress(data, "xwebStoAddress", template.xweb.sto);
    processAddress(data, "xwebSaleAddress", template.xweb.sale);
  }
  if (data["ladaStoAddresscountry"]) {
    template.lada = { sto: {}, sale: {} };
    processAddress(data, "ladaStoAddress", template.lada.sto);
    processAddress(data, "ladaSaleAddress", template.lada.sale);
  }

  if (aspCount.lenght !== 0) {
    const aspAddresses = [];
    for (const key in data) {
      if (key.includes("aspAddresscountry")) {
        const index = key.split("-")[0];
        let aspIndex = aspAddresses.findIndex((item) => item.index === index);
        if (aspIndex === -1) {
          aspIndex = aspAddresses.push({ index: index }) - 1;
        }
        const asp = {};
        processAddress(data, `${index}-aspAddress`, asp);
        aspAddresses[aspIndex] = asp;
      }
    }
    if (aspAddresses.length > 0) {
      template.asp = aspAddresses;
    }
  }
  return template;
};
